import React, { useState } from 'react';
// import { useIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import Icons from '../../components/Icons/Icons';
import { SafeHtml } from '../../components';

import css from './FAQPage.module.css';

// Section display order + human labels. Presentation lives in code (the Strapi
// `category` enum holds the machine keys). Any FAQ whose category is missing or
// unrecognized falls into the trailing "More Questions" group so nothing is ever
// hidden from the page.
const SECTION_ORDER = ['how-it-works', 'renters', 'owners', 'legal-safety', 'trust'];
const SECTION_LABELS = {
  'how-it-works': 'How BigLoads Works',
  renters: 'For Renters',
  owners: 'For Trailer Owners',
  'legal-safety': 'Legal, Safety & Liability',
  trust: 'Trust & Getting Help',
};
const FALLBACK_KEY = '__other__';
const FALLBACK_LABEL = 'More Questions';

const getField = (faq, key) => faq?.[key] ?? faq?.attributes?.[key];

function FAQSection(props) {
  const { faqsData } = props;

  // Track the open item by a stable unique id so the accordion works across groups.
  const [activeKey, setActiveKey] = useState(null);

  const handleAccordionClick = key => {
    setActiveKey(key === activeKey ? null : key);
  };

  // 1. Group FAQs by category (fallback bucket for missing/unknown categories).
  const groups = {};
  (faqsData || []).forEach(faq => {
    const cat = getField(faq, 'category');
    const key = SECTION_ORDER.includes(cat) ? cat : FALLBACK_KEY;
    if (!groups[key]) groups[key] = [];
    groups[key].push(faq);
  });

  // 2. Determine render order: known sections first (fixed order), fallback last.
  const orderedKeys = [
    ...SECTION_ORDER.filter(k => groups[k] && groups[k].length),
    ...(groups[FALLBACK_KEY] && groups[FALLBACK_KEY].length ? [FALLBACK_KEY] : []),
  ];

  // 3. Sort within each group by sortOrder (stable, missing sorts last).
  const bySortOrder = (a, b) => {
    const sa = getField(a, 'sortOrder');
    const sb = getField(b, 'sortOrder');
    const na = sa == null ? Number.MAX_SAFE_INTEGER : Number(sa);
    const nb = sb == null ? Number.MAX_SAFE_INTEGER : Number(sb);
    return na - nb;
  };

  // If NOTHING has a category yet (e.g. pre-tagging), render a single flat list
  // so the page is identical to the old behavior — never regress.
  const hasAnyCategory = orderedKeys.some(k => k !== FALLBACK_KEY);

  const renderCard = (faq, uniqueKey) => {
    const question = getField(faq, 'question');
    const answer = getField(faq, 'answer');
    const htmlDescription = getField(faq, 'htmlDescription');
    const isActive = activeKey === uniqueKey;

    return (
      <div
        key={uniqueKey}
        className={classNames(css.faqCard, { [css.activeFAQ]: isActive })}
        onClick={() => handleAccordionClick(uniqueKey)}
      >
        <div className={css.faqBody}>
          <span className={css.faqHeading}>{question} </span>{' '}
          {isActive &&
            (htmlDescription ? (
              <SafeHtml html={htmlDescription} className={css.faqDescription} />
            ) : (
              <div className={css.faqDescription}>{answer}</div>
            ))}
        </div>
        <span className={css.faqIcon}>
          {isActive ? (
            <span>
              <Icons name="faqOpenIcon" />
            </span>
          ) : (
            <span>
              <Icons name="faqCloseIcon" />
            </span>
          )}
        </span>
      </div>
    );
  };

  return (
    <div className={css.faqContentWrapper}>
      <div className={css.faqContent}>
        {hasAnyCategory ? (
          orderedKeys.map(key => {
            const items = groups[key].slice().sort(bySortOrder);
            const label = key === FALLBACK_KEY ? FALLBACK_LABEL : SECTION_LABELS[key];
            return (
              <div key={key} className={css.faqGroup}>
                <h2 className={css.faqSectionTitle}>{label}</h2>
                <hr className={css.faqSectionRule} />
                {items.map((faq, i) => {
                  const slug = getField(faq, 'slug');
                  const uniqueKey = `${key}-${slug || i}`;
                  return renderCard(faq, uniqueKey);
                })}
              </div>
            );
          })
        ) : (
          // No categories present at all -> flat list (legacy behavior, no regression).
          (faqsData || []).map((faq, i) => {
            const slug = getField(faq, 'slug');
            return renderCard(faq, `flat-${slug || i}`);
          })
        )}
      </div>
    </div>
  );
}

export default FAQSection;
